// src/pages/PrincipalDashboard.js
import React, { useEffect, useRef, useState } from "react";
import "../styles/PrincipalDashboard.css";
import api from "../services/api";

// sections (self-contained)
import ProfileCard from "../components/principal/ProfileCard";
import PhotosToReview from "../components/principal/PhotosToReview";
import StaffManager from "../components/principal/StaffManager";
import ParentInvite from "../components/principal/ParentInvite";
import WebsiteApiKey from "../components/principal/WebsiteApiKey";
import SubscriptionPanel from "../components/principal/SubscriptionPanel";
import YourPhotosGrid from "../components/principal/YourPhotosGrid";
import Tabs from "../components/Tabs";

const PrincipalDashboard = () => {
  const [profile, setProfile] = useState({
    email: "",
    displayName: "",
    schoolName: "",
    totalPhotos: 0,
    instagramUsername: null,
    schoolHashtag: null,
    websiteTechUsed: null,
    schoolId: null,
    principalName: null,
  });

  const [loading, setLoading] = useState(true);
  const [instagramMessage, setInstagramMessage] = useState("");
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("profile"); // for passing "active" to some panels

  const greeting = React.useMemo(() => {
    const h = new Date().getHours();
    if (h >= 5 && h < 12) return "Good morning";
    if (h >= 12 && h < 17) return "Good afternoon";
    return "Good evening";
  }, []);

  // fetch basic profile once; children handle their own data afterwards
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/profile");
        const {
          email,
          display_name,
          school_name,
          total_photos,
          instagram_username,
          school_hashtag,
          website_tech_used,
          school_id,
          principal_name,
        } = data;

        setProfile({
          email,
          displayName: display_name || email.split("@")[0],
          schoolName: school_name || "N/A",
          totalPhotos: total_photos || 0,
          instagramUsername: instagram_username || "Not connected",
          schoolHashtag: school_hashtag || null,
          websiteTechUsed: website_tech_used || null,
          schoolId: school_id || "No school Id assigned",
          principalName: principal_name || "N/A",
        });
      } catch (e) {
        console.error(e);
        setError("Failed to load profile.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // handle Stripe/Instagram return params → show message + clean URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get("success");
    const messageText = params.get("message");
    const errorText = params.get("error");
    const errorDescription = params.get("error_description");

    if (success === "true" && messageText) {
      setInstagramMessage(decodeURIComponent(messageText));
      setTimeout(() => {
        setInstagramMessage("");
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 5000);
    } else if (errorText && errorDescription) {
      setError(decodeURIComponent(errorDescription));
      setTimeout(() => {
        setError("");
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 4000);
    }
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <p>Loading...</p>
      </div>
    );
  }

  // Tab definitions (lazy; panels mount on first open and stay mounted)
  const tabs = [
    {
      id: "profile",
      label: "Your Profile",
      render: () => (
        <ProfileCard
          profile={profile}
          onProfileChange={setProfile}
          onError={setError}
        />
      ),
    },
    {
      id: "photos-review",
      label: "Photos to Review",
      render: () => <PhotosToReview onError={setError} />,
    },
    {
      id: "staff",
      label: "Staff Management",
      render: () => <StaffManager onError={setError} />,
    },
    {
      id: "parents",
      label: "Parent Management",
      render: () => <ParentInvite onError={setError} />,
    },
    {
      id: "website",
      label: "Your School’s Website",
      render: () => (
        <WebsiteApiKey
          active={activeTab === "website"}          // optional: lets the component lazy-fetch
          websiteTechUsed={profile.websiteTechUsed}
          schoolId={profile.schoolId}
          principalName={profile.principalName}
          onError={setError}
        />
      ),
    },
    {
      id: "subscription",
      label: "Subscription",
      render: () => <SubscriptionPanel />,
    },
    {
      id: "your-photos",
      label: "Your Uploaded Photos",
      render: () => (
        <YourPhotosGrid
          active={activeTab === "your-photos"}      // optional: first-load when opened
          onError={setError}
        />
      ),
    },
  ];

  return (
    <div className="principal-dashboard-container">
      <h1>{greeting}, {profile.displayName}!</h1>

      <Tabs
        tabs={tabs}
        defaultTabId="profile"
        lazy
        keepAlive
        storageKey="principal-dashboard.activeTab"
        onChange={(id) => setActiveTab(id)}
      />

      {instagramMessage && (
        <p className="success-message" aria-live="polite">
          {instagramMessage}
        </p>
      )}
      {error && (
        <p className="error-message" aria-live="polite">
          {error}
        </p>
      )}
    </div>
  );
};

export default PrincipalDashboard;
